/**
 * ScheduledTemplatePicker
 * -----------------------------------------------------------------------------
 * Componente reutilizável (inline, sem dialog wrapper) para selecionar uma
 * template aprovada do WhatsApp Oficial e preencher suas variáveis de BODY.
 *
 * Reaproveita a estrutura conceitual de `SendTemplateDialog` (busca via
 * `whatsapp_templates` filtrada por `inbox_id` + `status=APPROVED`), mas
 * simplificada para o contexto de agendamento.
 *
 * @version 1.1.0
 * RELEASE: 2026-06-12 v1.1.0 - Adiciona busca textual no seletor (Command +
 *          Popover) e preview rico estilo WhatsApp (header com mídia, body
 *          com variáveis renderizadas, footer e botões com ícones por tipo).
 * RELEASE: 2026-06-12 v1.0.0 - Criação para suportar agendamento de templates
 *          em inboxes WhatsApp Oficial (janela 24h + fallback).
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Loader2,
  RefreshCw,
  FileText,
  AlertCircle,
  ChevronsUpDown,
  Check,
  ExternalLink,
  Phone,
  Copy,
  CreditCard,
  MessageSquare,
  ImageIcon,
  VideoIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/logger';

/**
 * Valor controlado retornado pelo picker.
 */
export interface ScheduledTemplateValue {
  /** Nome da template Meta (ex.: "appointment_reminder"). */
  templateName: string;
  /** Idioma da template (ex.: "pt_BR"). */
  templateLanguage: string;
  /** Valores das variáveis do BODY na ordem ({{1}}, {{2}}…). */
  bodyParams: string[];
  /** Texto renderizado do BODY (útil para preview). */
  bodyTextRendered: string;
}

interface WhatsAppTemplateRow {
  id: string;
  inbox_id: string;
  template_id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  components: unknown;
}

interface Props {
  inboxId?: string;
  value: ScheduledTemplateValue | null;
  onChange: (next: ScheduledTemplateValue | null) => void;
  disabled?: boolean;
  /** Label customizado para o select (ex.: "Template de fallback"). */
  selectLabel?: string;
}

/** Extrai componente do template por tipo. */
function getComponent(components: unknown, type: string): any | null {
  if (!Array.isArray(components)) return null;
  return components.find((c: any) => c?.type === type) || null;
}

/** Extrai variáveis numéricas ({{1}}, {{2}}…) do texto, sem duplicar. */
function extractBodyVars(text: string): string[] {
  if (!text) return [];
  const matches = text.match(/\{\{(\d+)\}\}/g) || [];
  return [...new Set(matches)].sort((a, b) => {
    const na = parseInt(a.replace(/\D/g, ''), 10);
    const nb = parseInt(b.replace(/\D/g, ''), 10);
    return na - nb;
  });
}

/** Substitui {{n}} pelos valores fornecidos. */
function renderBody(text: string, values: Record<string, string>): string {
  if (!text) return '';
  let out = text;
  for (const [k, v] of Object.entries(values)) {
    if (!v) continue;
    out = out.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v);
  }
  return out;
}

/**
 * Formata texto destacando variáveis {{n}} ainda não preenchidas.
 * Usado no preview rico para dar feedback visual de placeholders.
 */
function formatTextWithVariables(text: string): React.ReactNode {
  if (!text) return null;
  const parts = text.split(/(\{\{\d+\}\})/g);
  return parts.map((part, idx) => {
    if (/\{\{\d+\}\}/.test(part)) {
      return (
        <span
          key={idx}
          className="bg-primary/20 text-primary px-1 rounded text-xs font-mono"
        >
          {part}
        </span>
      );
    }
    return <span key={idx}>{part}</span>;
  });
}

/** Ícone de tipo de botão no preview. */
function ButtonTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'URL':
      return <ExternalLink className="h-3.5 w-3.5" />;
    case 'PHONE_NUMBER':
      return <Phone className="h-3.5 w-3.5" />;
    case 'COPY_CODE':
      return <Copy className="h-3.5 w-3.5" />;
    case 'ORDER_DETAILS':
      return <CreditCard className="h-3.5 w-3.5" />;
    default:
      return <MessageSquare className="h-3.5 w-3.5" />;
  }
}

/**
 * Preview rico estilo WhatsApp: header (mídia ou texto), body com variáveis
 * destacadas, footer e botões dinâmicos. Reproduz o balão usado no wizard
 * de criação de templates, porém autônomo para evitar regressão no wizard.
 */
function TemplateRichPreview({ tpl, bodyValues }: { tpl: WhatsAppTemplateRow; bodyValues: Record<string, string> }) {
  const header = getComponent(tpl.components, 'HEADER');
  const body = getComponent(tpl.components, 'BODY');
  const footer = getComponent(tpl.components, 'FOOTER');
  const buttonsComp = getComponent(tpl.components, 'BUTTONS');

  const headerFormat: string = header?.format || 'NONE';
  const headerText: string = header?.text || '';
  // Tentar extrair URL de mídia de exemplo da Meta (formatos comuns no JSON)
  const headerMediaUrl: string | undefined =
    header?.example?.header_url?.[0] ||
    header?.example?.header_handle?.[0] ||
    undefined;

  const bodyText: string = body?.text || '';
  const renderedBody = renderBody(bodyText, bodyValues);
  const footerText: string = footer?.text || '';
  const buttons: any[] = Array.isArray(buttonsComp?.buttons) ? buttonsComp.buttons : [];

  const hasHeader = headerFormat !== 'NONE' && (headerText || headerFormat !== 'TEXT');

  return (
    <div className="rounded-md border p-3" style={{ backgroundColor: '#e5ddd5' }}>
      <div className="max-w-[280px] mx-auto">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden text-slate-900">
          {/* Header */}
          {hasHeader && (
            <div className="border-b border-slate-200 overflow-hidden">
              {headerFormat === 'TEXT' && (
                <div className="px-3 py-2 font-semibold text-sm">
                  {formatTextWithVariables(headerText)}
                </div>
              )}
              {headerFormat === 'IMAGE' && (
                headerMediaUrl ? (
                  <div className="relative w-full aspect-video bg-muted/50 overflow-hidden">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={headerMediaUrl} alt="Header" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="p-6 bg-gradient-to-br from-slate-100 to-slate-200 flex flex-col items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                      <ImageIcon className="h-7 w-7 text-blue-500" />
                    </div>
                    <span className="text-[10px] text-slate-500 mt-2">Imagem do header</span>
                  </div>
                )
              )}
              {headerFormat === 'VIDEO' && (
                headerMediaUrl ? (
                  <video src={headerMediaUrl} controls className="w-full aspect-video object-contain bg-black" />
                ) : (
                  <div className="p-6 bg-gradient-to-br from-slate-100 to-slate-200 flex flex-col items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center">
                      <VideoIcon className="h-7 w-7 text-purple-500" />
                    </div>
                    <span className="text-[10px] text-slate-500 mt-2">Vídeo do header</span>
                  </div>
                )
              )}
              {headerFormat === 'DOCUMENT' && (
                <div className="p-3 bg-gradient-to-br from-red-50 to-orange-50 flex items-center gap-3">
                  <div className="w-10 h-12 bg-white rounded shadow-sm flex items-center justify-center border">
                    <FileText className="h-5 w-5 text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">
                      {headerMediaUrl ? (headerMediaUrl.split('/').pop()?.split('?')[0] || 'documento.pdf') : 'documento.pdf'}
                    </p>
                    <p className="text-[10px] text-slate-500">PDF Document</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Body */}
          <div className="px-3 py-2 text-sm whitespace-pre-wrap text-slate-900">
            {renderedBody ? formatTextWithVariables(renderedBody) : (
              <span className="text-slate-400 italic">(body vazio)</span>
            )}
          </div>

          {/* Footer */}
          {footerText && (
            <div className="px-3 py-2 text-xs text-slate-500 border-t border-slate-200">
              {footerText}
            </div>
          )}

          {/* Botões */}
          {buttons.length > 0 && (
            <div className="border-t">
              {buttons.map((btn, idx) => (
                <div
                  key={idx}
                  className={cn(
                    'w-full px-3 py-2.5 text-sm font-medium',
                    'flex items-center justify-center gap-2',
                    idx > 0 && 'border-t',
                    btn.type === 'ORDER_DETAILS' ? 'text-green-600' : 'text-blue-500',
                  )}
                >
                  <ButtonTypeIcon type={btn.type} />
                  {btn.text || 'Botão'}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Timestamp */}
        <div className="text-right mt-1">
          <span className="text-[10px] text-slate-500">
            {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </div>
    </div>
  );
}

export function ScheduledTemplatePicker({
  inboxId,
  value,
  onChange,
  disabled = false,
  selectLabel = 'Template aprovada',
}: Props) {
  const [syncing, setSyncing] = useState(false);
  const [open, setOpen] = useState(false);

  // -------------------------------------------------------------------------
  // Busca templates APPROVED da inbox
  // -------------------------------------------------------------------------
  const {
    data: templates = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['scheduled-tpl-picker', inboxId],
    enabled: !!inboxId,
    staleTime: 30_000,
    queryFn: async () => {
      if (!inboxId) return [] as WhatsAppTemplateRow[];
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('id, inbox_id, template_id, name, language, category, status, components')
        .eq('inbox_id', inboxId)
        .eq('status', 'APPROVED')
        .order('name');
      if (error) {
        logger.error('[ScheduledTemplatePicker] erro ao listar templates', error.message);
        throw error;
      }
      return (data || []) as WhatsAppTemplateRow[];
    },
  });

  // Template atualmente selecionado (objeto completo).
  const selectedTpl = useMemo(() => {
    if (!value) return null;
    return (
      templates.find(
        (t) => t.name === value.templateName && t.language === value.templateLanguage,
      ) || null
    );
  }, [templates, value]);

  // Extrai texto do BODY e variáveis numéricas declaradas.
  const { bodyText, bodyVars } = useMemo(() => {
    const body = getComponent(selectedTpl?.components, 'BODY');
    const txt: string = body?.text || '';
    return { bodyText: txt, bodyVars: extractBodyVars(txt) };
  }, [selectedTpl]);

  // Estado local de variáveis preenchidas.
  const [varValues, setVarValues] = useState<Record<string, string>>({});

  // Quando o template muda, reinicia o estado de variáveis com o que veio em `value`.
  useEffect(() => {
    if (!selectedTpl) {
      setVarValues({});
      return;
    }
    const next: Record<string, string> = {};
    bodyVars.forEach((v, idx) => {
      const key = v.replace(/[{}]/g, '');
      next[key] = value?.bodyParams?.[idx] || '';
    });
    setVarValues(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTpl?.id]);

  /** Sincroniza templates com o provider via edge function existente. */
  const handleSync = async () => {
    if (!inboxId) return;
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-templates', {
        body: { inbox_id: inboxId },
      });
      if (error) throw error;
      toast.success(`Templates sincronizados: ${data?.synced || 0}`);
      await refetch();
    } catch (err) {
      logger.error('[ScheduledTemplatePicker] erro ao sincronizar', err instanceof Error ? err.message : String(err));
      toast.error('Erro ao sincronizar templates');
    } finally {
      setSyncing(false);
    }
  };

  /** Handler ao escolher um template (ou limpar). */
  const handlePick = (templateId: string | null) => {
    setOpen(false);
    if (!templateId) {
      onChange(null);
      return;
    }
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) {
      onChange(null);
      return;
    }
    const body = getComponent(tpl.components, 'BODY');
    const txt: string = body?.text || '';
    const vars = extractBodyVars(txt);
    const params = vars.map(() => '');
    onChange({
      templateName: tpl.name,
      templateLanguage: tpl.language,
      bodyParams: params,
      bodyTextRendered: txt,
    });
  };

  /** Atualiza uma variável e propaga para o pai. */
  const updateVar = (key: string, val: string) => {
    const next = { ...varValues, [key]: val };
    setVarValues(next);
    if (!selectedTpl) return;
    const orderedParams = bodyVars.map((v) => next[v.replace(/[{}]/g, '')] || '');
    onChange({
      templateName: selectedTpl.name,
      templateLanguage: selectedTpl.language,
      bodyParams: orderedParams,
      bodyTextRendered: renderBody(bodyText, next),
    });
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (!inboxId) {
    return (
      <p className="text-[11px] text-muted-foreground">
        Inbox da conversa não identificada — não é possível listar templates.
      </p>
    );
  }

  // Texto exibido no trigger do select-com-busca
  const triggerLabel = selectedTpl
    ? `${selectedTpl.name}`
    : isLoading
      ? 'Carregando templates…'
      : templates.length === 0
        ? 'Nenhuma template aprovada'
        : 'Selecione uma template';

  return (
    <div className="space-y-3">
      {/* Linha: combobox com busca + Sincronizar */}
      <div className="space-y-1.5">
        <Label className="text-xs">{selectLabel}</Label>
        <div className="flex items-center gap-1.5">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={open}
                disabled={disabled || isLoading}
                className="h-9 text-xs flex-1 justify-between font-normal"
              >
                <span className="truncate flex items-center gap-1.5">
                  {selectedTpl && (
                    <span className="text-muted-foreground text-[10px]">
                      {selectedTpl.language} · {selectedTpl.category}
                    </span>
                  )}
                  <span className={cn('truncate', !selectedTpl && 'text-muted-foreground')}>
                    {triggerLabel}
                  </span>
                </span>
                <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="p-0 w-[--radix-popover-trigger-width] min-w-[280px]"
              align="start"
            >
              <Command
                filter={(value, search) => {
                  // value armazena "name idioma categoria" em lowercase
                  if (value.includes(search.toLowerCase())) return 1;
                  return 0;
                }}
              >
                <CommandInput placeholder="Buscar template por nome…" className="h-9 text-xs" />
                <CommandList>
                  <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">
                    Nenhuma template encontrada.
                  </CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="__none__ sem template"
                      onSelect={() => handlePick(null)}
                      className="text-xs"
                    >
                      <Check
                        className={cn(
                          'mr-2 h-3.5 w-3.5',
                          !selectedTpl ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="italic text-muted-foreground">— Sem template —</span>
                    </CommandItem>
                    {templates.map((t) => {
                      const searchValue =
                        `${t.name} ${t.language} ${t.category}`.toLowerCase();
                      return (
                        <CommandItem
                          key={t.id}
                          value={searchValue}
                          onSelect={() => handlePick(t.id)}
                          className="text-xs"
                        >
                          <Check
                            className={cn(
                              'mr-2 h-3.5 w-3.5 shrink-0',
                              selectedTpl?.id === t.id ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          <span className="font-medium truncate">{t.name}</span>
                          <span className="text-muted-foreground ml-1.5 text-[10px] shrink-0">
                            {t.language} · {t.category}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={handleSync}
            disabled={disabled || syncing}
            title="Sincronizar templates com a Meta"
          >
            {syncing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>

      {/* Bloco quando há um template selecionado */}
      {selectedTpl && (
        <div className="rounded-md border bg-muted/20 p-3 space-y-3">
          {/* Preview rico estilo WhatsApp */}
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <FileText className="h-3 w-3" /> Pré-visualização
            </Label>
            <TemplateRichPreview tpl={selectedTpl} bodyValues={varValues} />
          </div>

          {/* Inputs por variável */}
          {bodyVars.length > 0 ? (
            <div className="space-y-2">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Preencha as variáveis
              </Label>
              {bodyVars.map((v) => {
                const key = v.replace(/[{}]/g, '');
                return (
                  <div key={key} className="grid grid-cols-[60px,1fr] items-center gap-2">
                    <Badge variant="secondary" className="font-mono text-[10px] justify-center">
                      {v}
                    </Badge>
                    <Input
                      value={varValues[key] || ''}
                      onChange={(e) => updateVar(key, e.target.value)}
                      placeholder={`Valor para ${v}`}
                      className="h-8 text-xs"
                      disabled={disabled}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <AlertCircle className="h-3 w-3" />
              Esta template não possui variáveis no body.
            </div>
          )}
        </div>
      )}

      {/* Vazio: instrução para sincronizar */}
      {!isLoading && templates.length === 0 && (
        <div
          className={cn(
            'rounded-md border border-dashed p-3 text-[11px] text-muted-foreground',
            'flex items-start gap-2',
          )}
        >
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div>
            Nenhuma template aprovada nesta inbox. Clique no ícone{' '}
            <RefreshCw className="h-3 w-3 inline -mt-0.5" /> para sincronizar
            com a Meta ou crie/aprove uma template antes de agendar.
          </div>
        </div>
      )}
    </div>
  );
}
